/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* Migant lightweight wishlist */
;(() => {
  const KEY = 'migantWishlist';
  let toastTimer;

  const styles = `
    .product-card-wrapper > .card { position: relative; }
    .migant-wishlist-button {
      position:absolute;
      top:1.2rem;
      right:1.2rem;
      bottom:auto;
      z-index:5;
      width:4.2rem;
      height:4.2rem;
      padding:0;
      border:0;
      border-radius:50%;
      background:rgba(255,255,255,.94);
      color:#111;
      display:flex;
      align-items:center;
      justify-content:center;
      cursor:pointer;
      box-shadow:0 1px 5px rgba(0,0,0,.14);
      transition:transform .15s ease,background .15s ease;
    }
    .migant-wishlist-button:hover { transform:scale(1.05); background:#fff; }
    .migant-wishlist-button:focus-visible { outline:2px solid currentColor; outline-offset:2px; }
    .migant-wishlist-button svg { width:2.2rem; height:2.2rem; pointer-events:none; }
    .migant-wishlist-button path {
      fill:transparent;
      stroke:currentColor;
      stroke-width:1.7;
      stroke-linecap:round;
      stroke-linejoin:round;
      transition:fill .15s ease;
    }
    .migant-wishlist-button.is-saved path { fill:currentColor; }
    .migant-view-wishlist-link {
      display:block;
      margin-top:.8rem;
      text-align:center;
      color:rgb(var(--color-foreground));
      text-decoration:underline;
      text-underline-offset:.25rem;
      font-size:1.3rem;
    }
    .migant-wishlist-toast {
      position:fixed;
      right:2rem;
      bottom:2rem;
      z-index:9999;
      max-width:min(36rem,calc(100vw - 3.2rem));
      padding:1.2rem 1.4rem;
      background:#111;
      color:#fff;
      display:flex;
      align-items:center;
      gap:1.2rem;
      box-shadow:0 4px 18px rgba(0,0,0,.2);
      opacity:0;
      transform:translateY(1rem);
      pointer-events:none;
      transition:opacity .18s ease,transform .18s ease;
    }
    .migant-wishlist-toast.is-visible { opacity:1; transform:none; pointer-events:auto; }
    .migant-wishlist-toast a { color:#fff; font-weight:700; text-decoration:underline; white-space:nowrap; }
    @media screen and (max-width:749px) {
      .migant-wishlist-button { top:.8rem; right:.8rem; bottom:auto; width:3.8rem; height:3.8rem; }
      .migant-wishlist-button svg { width:2rem; height:2rem; }
      .migant-wishlist-toast { left:1.6rem; right:1.6rem; bottom:1.6rem; max-width:none; }
    }
  `;

  if (!document.getElementById('migant-wishlist-styles')) {
    const style = document.createElement('style');
    style.id = 'migant-wishlist-styles';
    style.textContent = styles;
    document.head.appendChild(style);
  }

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (_) {}
  };

  const normalizeKey = (value) => {
    try { return new URL(value, window.location.origin).pathname; }
    catch (_) { return value; }
  };

  const getProductKey = (card) => {
    const link = card.querySelector('.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"]');
    if (!link) return null;
    return normalizeKey(link.href || link.getAttribute('href'));
  };

  const updateButton = (button, saved) => {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? 'Remove from saved items' : 'Save item');
    button.title = saved ? 'Remove from saved items' : 'Save item';
  };

  const ensurePdpLink = () => {
    const wrap = document.querySelector('.migant-pdp-wishlist-wrap');
    if (!wrap || wrap.querySelector('.migant-view-wishlist-link')) return;
    const link = document.createElement('a');
    link.className = 'migant-view-wishlist-link';
    link.href = '/pages/wishlist';
    link.textContent = 'View wishlist';
    wrap.appendChild(link);
  };

  const showToast = (saved) => {
    let toast = document.getElementById('migant-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'migant-wishlist-toast';
      toast.className = 'migant-wishlist-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML = saved
      ? '<span>Saved to wishlist.</span><a href="/pages/wishlist">View wishlist</a>'
      : '<span>Removed from wishlist.</span>';
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  const init = () => {
    const items = readWishlist().map(normalizeKey);
    document.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (card.querySelector('[data-migant-wishlist]')) return;
      const key = getProductKey(card);
      const mount = card.querySelector(':scope > .card') || card.querySelector('.card');
      if (!key || !mount) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'migant-wishlist-button';
      button.dataset.migantWishlist = '';
      button.dataset.productKey = key;
      button.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M12 20.25s-7.5-4.35-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.85c0 5.85-7.5 10.2-7.5 10.2Z"></path>
        </svg>`;
      updateButton(button, items.includes(key));
      mount.appendChild(button);
    });

    document.querySelectorAll('[data-migant-wishlist]').forEach((button) => {
      const key = normalizeKey(button.dataset.productKey || '');
      if (key) {
        button.dataset.productKey = key;
        updateButton(button, items.includes(key));
      }
    });

    ensurePdpLink();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-migant-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const key = normalizeKey(button.dataset.productKey || '');
    if (!key) return;

    const items = readWishlist().map(normalizeKey);
    const index = items.indexOf(key);
    let saved;
    if (index >= 0) {
      items.splice(index, 1);
      saved = false;
    } else {
      items.push(key);
      saved = true;
    }
    writeWishlist([...new Set(items)]);

    document.querySelectorAll('[data-migant-wishlist]').forEach((otherButton) => {
      if (normalizeKey(otherButton.dataset.productKey || '') === key) updateButton(otherButton, saved);
    });

    showToast(saved);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once:true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      init();
    });
  }).observe(document.documentElement, { childList:true, subtree:true });
})();

/* Migant wishlist header shortcut + live count */
;(() => {
  const KEY = 'migantWishlist';

  const readCount = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? new Set(value).size : 0;
    } catch (_) {
      return 0;
    }
  };

  const ensureStyles = () => {
    if (document.getElementById('migant-wishlist-header-styles')) return;
    const style = document.createElement('style');
    style.id = 'migant-wishlist-header-styles';
    style.textContent = `
      .header__icon--migant-wishlist { position:relative; }
      .header__icon--migant-wishlist svg { width:2.2rem; height:2.2rem; }
      .header__icon--migant-wishlist path {
        fill:none;
        stroke:currentColor;
        stroke-width:1.7;
        stroke-linecap:round;
        stroke-linejoin:round;
      }
      .migant-wishlist-count-bubble[hidden] { display:none; }
    `;
    document.head.appendChild(style);
  };

  const ensureIcon = () => {
    const icons = document.querySelector('.header__icons');
    const cart = document.getElementById('cart-icon-bubble');
    if (!icons || !cart) return null;

    let link = icons.querySelector('[data-migant-wishlist-header]');
    if (!link) {
      link = document.createElement('a');
      link.href = '/pages/wishlist';
      link.className = 'header__icon header__icon--migant-wishlist link focus-inset';
      link.dataset.migantWishlistHeader = '';
      link.setAttribute('aria-label', 'Wishlist');
      link.innerHTML = `
        <span class="svg-wrapper" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 20.25s-7.5-4.35-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.85c0 5.85-7.5 10.2-7.5 10.2Z"></path>
          </svg>
        </span>
        <span class="visually-hidden">Wishlist</span>
        <div class="cart-count-bubble migant-wishlist-count-bubble" data-migant-wishlist-count hidden>
          <span aria-hidden="true">0</span>
          <span class="visually-hidden" data-migant-wishlist-count-label>0 saved items</span>
        </div>`;

      const account = icons.querySelector('shopify-account.header__icon--account');
      if (account) account.insertAdjacentElement('afterend', link);
      else cart.insertAdjacentElement('beforebegin', link);
    }
    return link;
  };

  const updateCount = () => {
    const link = ensureIcon();
    if (!link) return;
    const count = readCount();
    const bubble = link.querySelector('[data-migant-wishlist-count]');
    const number = bubble?.querySelector('[aria-hidden="true"]');
    const label = bubble?.querySelector('[data-migant-wishlist-count-label]');
    if (number) number.textContent = count < 100 ? String(count) : '99+';
    if (label) label.textContent = `${count} saved ${count === 1 ? 'item' : 'items'}`;
    if (bubble) bubble.hidden = count === 0;
    link.setAttribute('aria-label', count ? `Wishlist, ${count} saved ${count === 1 ? 'item' : 'items'}` : 'Wishlist');
  };

  const init = () => {
    ensureStyles();
    updateCount();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once:true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) updateCount();
  });

  let queued = false;
  new MutationObserver((mutations) => {
    if (!mutations.some((mutation) =>
      mutation.type === 'attributes' ||
      [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.('.header__icons, [data-migant-wishlist]') || node.querySelector?.('.header__icons, [data-migant-wishlist]')))
    )) return;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      updateCount();
    });
  }).observe(document.documentElement, { childList:true, subtree:true, attributes:true, attributeFilter:['class'] });
})();
